import { CheckCircle2, Clock, RefreshCw, Undo2, WalletCards, XCircle } from "lucide-react";
import { credits, rupees } from "../products/types";
import { formatWhen } from "./format-when";
import { ORDER_STATUS_LABEL, OrderStatusBadge } from "./order-status-badge";
import { OrderTotalsBreakdown } from "./order-totals-breakdown";
import type { OrderDetails, OrderEventSource, OrderStatus } from "./types";

const HEADLINE: Record<OrderStatus, { icon: typeof Clock; tone: string; subtitle: string }> = {
  payment_successful: { icon: CheckCircle2, tone: "text-success", subtitle: "Thank you. Your payment was received." },
  payment_pending: { icon: Clock, tone: "text-warning", subtitle: "We are waiting for the payment to be confirmed." },
  payment_failed: { icon: XCircle, tone: "text-danger", subtitle: "The payment did not go through. No money was taken." },
  cancelled: { icon: XCircle, tone: "text-ink-3", subtitle: "This order was cancelled because the cart changed." },
  partially_refunded: { icon: Undo2, tone: "text-info", subtitle: "Part of this order was refunded." },
  refunded: { icon: Undo2, tone: "text-ink-2", subtitle: "This order was refunded." },
};

const SOURCE_LABEL: Record<OrderEventSource, string> = {
  placed: "Order placed",
  browser: "Confirmed after checkout",
  webhook: "Confirmed by Razorpay",
  refresh: "Confirmed on re-check",
  cart_changed: "Cart changed",
  refund: "Refund",
  reconcile: "Checked with Razorpay",
};

/** Where the credits the order buys stand: added to the balance, or waiting for the payment. */
function CreditsNotice({ order }: { order: OrderDetails }) {
  if (order.credits_granted_at) {
    return (
      <p className="flex items-center gap-2 rounded-lg bg-success-soft px-3 py-2.5 text-sm font-medium text-success">
        <WalletCards className="h-4 w-4 shrink-0" />
        {credits(order.credits)} credits added to your balance
      </p>
    );
  }
  if (order.status === "cancelled") return null;
  return (
    <p className="flex items-center gap-2 rounded-lg bg-soft px-3 py-2.5 text-sm text-ink-2">
      <WalletCards className="h-4 w-4 shrink-0 text-ink-3" />
      {order.status === "payment_successful"
        ? `${credits(order.credits)} credits are being added to your balance.`
        : `${credits(order.credits)} credits will be added once the payment is confirmed.`}
    </p>
  );
}

interface OrderCardProps {
  order: OrderDetails;
  onRecheck: () => void;
  rechecking: boolean;
}

/** One order: what was bought, what it cost, and how the payment went. */
export function OrderCard({ order, onRecheck, rechecking }: OrderCardProps) {
  const headline = HEADLINE[order.status];
  const Icon = headline.icon;
  const settled = order.status === "payment_successful" || order.refunded_amount > 0;

  return (
    <section className="space-y-6 rounded-2xl border border-line bg-elevated p-6 shadow-sm" aria-label={`Order ${order.reference_number}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-1 h-6 w-6 shrink-0 ${headline.tone}`} />
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-ink">{ORDER_STATUS_LABEL[order.status]}</h1>
          <p className="mt-1 text-sm text-ink-2">{headline.subtitle}</p>
          <p className="mt-2 text-xs text-ink-3">
            Order <span className="font-mono font-semibold text-ink-2">{order.reference_number}</span> · {formatWhen(order.created_at)}
          </p>
        </div>
        <OrderStatusBadge status={order.status} />
      </div>

      {order.credits > 0 && <CreditsNotice order={order} />}

      <ul className="divide-y divide-line border-y border-line">
        {order.items.map((item) => (
          <li key={item.line_item_id} className="flex justify-between gap-4 py-3 text-sm">
            <div>
              <p className="font-medium text-ink">{item.name}</p>
              {item.rate_per_minute !== null && item.credits !== null ? (
                <p className="tabular-nums text-ink-3">
                  {rupees(item.amount)} at {rupees(item.rate_per_minute)} / min · {credits(item.credits)} credits
                </p>
              ) : (
                <p className="tabular-nums text-ink-3">{item.quantity} × {rupees(item.unit_price)}</p>
              )}
            </div>
            <span className="font-semibold tabular-nums text-ink">{rupees(item.amount)}</span>
          </li>
        ))}
      </ul>

      <OrderTotalsBreakdown totals={order.totals} totalLabel={settled ? "Total paid" : "Total payable"} />

      {settled && (
        <p className="text-xs text-ink-3">
          Paid{order.payment_method ? ` via ${order.payment_method.toUpperCase()}` : ""}
          {order.paid_at ? ` on ${formatWhen(order.paid_at)}` : ""}
          {order.ref_payment_id ? ` · Payment ID ${order.ref_payment_id}` : ""}
        </p>
      )}
      {order.refunded_amount > 0 && (
        <p className="rounded-lg bg-soft px-3 py-2 text-sm text-ink-2">
          Refunded <span className="font-semibold tabular-nums text-ink">{rupees(order.refunded_amount)}</span>
          {order.refunded_credits > 0 && ` · ${credits(order.refunded_credits)} unused credits taken back`}
        </p>
      )}
      {order.status === "payment_failed" && order.failure_reason && (
        <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">{order.failure_reason}</p>
      )}
      {(order.status === "payment_pending" || order.status === "payment_failed") && (
        <button
          onClick={onRecheck}
          disabled={rechecking}
          className="inline-flex h-10 items-center gap-2 rounded-lg border border-line px-4 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-wait disabled:opacity-60"
        >
          <RefreshCw className="h-4 w-4" /> {rechecking ? "Checking…" : "Check payment status"}
        </button>
      )}

      <div>
        <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-ink-3">Status history</h2>
        <ol className="space-y-2" aria-label="Status history">
          {order.history.map((change, i) => (
            <li key={i} className="flex justify-between gap-4 text-sm">
              <span className="text-ink">
                {ORDER_STATUS_LABEL[change.status]}
                <span className="text-ink-3"> · {change.note ?? SOURCE_LABEL[change.source]}</span>
              </span>
              <span className="whitespace-nowrap tabular-nums text-ink-3">{formatWhen(change.at)}</span>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
